<template>
    <Card
        v-if="card"
        ref="chartContainer"
        class="chart-container"
        overlay
        :loading="loading || updating"
        :no-padding="noPadding"
        :tooltip="tooltip"
    >
        <ErrorMessage :value="error" />
        <slot name="chart">
            <ECharts ref="chart" v-bind="attrs" :init-options="initOptions" v-on="listeners" />
        </slot>
        <template #header>
            <div class="d-flex gap-2 align-items-center">
                <span v-if="header">{{ header }}</span>
                <ButtonIcon
                    icon="download"
                    variant="light"
                    :icon-size="14"
                    class="p-1 rounded-max mb-auto width-25 height-25"
                    :title="$t('export')"
                    @click="exportChart"
                />
                <ButtonIcon
                    icon="maximize"
                    variant="light"
                    :icon-size="14"
                    class="p-1 rounded-max mb-auto width-25 height-25"
                    :title="$t('fullscreen')"
                    @click="fullscreenChart"
                />
            </div>
        </template>
        <template #nav><slot name="nav"></slot></template>
    </Card>
    <b-overlay v-else :show="updating" rounded="sm">
        <ECharts ref="chart" v-bind="attrs" :init-options="initOptions" v-on="listeners" />
    </b-overlay>
</template>

<script>
    import feather from 'feather-icons';
    import mergeWith from 'lodash/mergeWith';
    import ECharts from 'vue-echarts';
    import * as echarts from 'echarts/core';
    import enLocale from 'echarts/i18n/langEN-obj';
    import plLocale from 'echarts/i18n/langPL-obj';
    import { getEffectiveLocale } from '@/i18n/i18n';

    /* eslint-disable no-underscore-dangle */

    const makeSvgDataUrl = svgString => `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
    const btnIconSvg = feather.icons['arrow-up-right'].toSvg({ stroke: '#007aff' });
    const btnIconIcon = makeSvgDataUrl(btnIconSvg);

    echarts.registerTransform({
        type: 'echarts:percent', // nazwa musi być taka sama jak w dataset.transform
        transform(params) {
            const upstream = params.upstream;
            const dimsInput = params.config?.dimension;
            // jeśli nie ma tablicy dimension — zostaw surowe dane
            if (!Array.isArray(dimsInput)) {
                return { data: upstream.getRawData() };
            }

            // 1) przygotuj tylko te wymiary, które istnieją:
            const dims = dimsInput.map(name => ({ name, info: upstream.getDimensionInfo(name) })).filter(d => d.info);

            const out = [];

            // 2) dla każdego wiersza danych
            for (let i = 0; i < upstream.count(); i++) {
                const row = upstream.getRawDataItem(i);

                // policz sumę tylko po poprawnych wymiarach
                const sum = dims.reduce((acc, { info }) => acc + upstream.retrieveValueFromItem(row, info.index), 0);

                // zachowaj surowe w row pod __raw
                const newRow = { ...row, __raw: row };

                // jeśli suma ≠ 0, przelicz każdy wymiar na procent
                if (sum) {
                    dims.forEach(({ name, info }) => {
                        const v = upstream.retrieveValueFromItem(row, info.index);
                        newRow[name] = (v / sum) * 100;
                    });
                } else {
                    // suma 0 → wszystkie wartości 0%
                    dims.forEach(({ name }) => {
                        newRow[name] = 0;
                    });
                }

                out.push(newRow);
            }

            return { data: out };
        },
    });

    /**
     * Customizer dla mergeWith – jeśli źródło jest tablicą, łączymy każdy element z wartością domyślną.
     */
    const customizer = (objValue, srcValue) => {
        if (typeof objValue === 'object' && !Array.isArray(objValue) && Array.isArray(srcValue)) {
            return srcValue.map(item => mergeWith({}, objValue, item, customizer));
        }
        return undefined;
    };

    const substringFormatter = (value, maxChars = 50) => {
        if (typeof value === 'string') {
            if (value.length > maxChars) {
                return `${value.substring(0, maxChars)}...`;
            }
        }
        return value;
    };

    const RANGES = ['hour', 'day', 'week', 'month', 'quarter', 'year'];

    export default {
        name: 'EChart',
        inheritAttrs: false,
        components: { ECharts },
        props: {
            error: {
                type: [Error, Object],
                default: null,
            },
            header: {
                type: String,
                default: null,
            },
            tooltip: {
                type: String,
                default: null,
            },
            card: {
                type: Boolean,
                default: false,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            width: {
                type: [String, Number],
                default: '100%',
            },
            height: {
                type: [String, Number],
                default: '400px',
            },
            type: {
                type: String,
                default: undefined,
            },
            noPadding: {
                type: Boolean,
                default: false,
            },
            groupBy: {
                type: String,
                default: 'day',
                validator: value => RANGES.includes(value),
            },
        },
        data() {
            return {
                initOptions: { locale: 'PL' },
                updating: false,
                defaultOptions: {
                    grid: {
                        left: 7,
                        right: 7,
                        top: 20,
                        bottom: 20,
                        containLabel: false,
                    },
                    tooltip: {
                        show: true,
                        valueFormatter: value => value,
                        appendToBody: true,
                        confine: true,
                    },
                    xAxis: {
                        type: 'value',
                        axisLabel: {
                            formatter: val => substringFormatter(val, 22),
                        },
                    },
                    yAxis: {
                        type: 'value',
                        nameRotate: 90,
                        nameLocation: 'middle',
                        nameGap: 60,
                        nameTextStyle: {
                            align: 'center',
                            verticalAlign: 'middle',
                            lineHeight: 0,
                        },
                        axisLabel: {
                            // formatter: val => substringFormatter(val),
                            width: 200,
                            overflow: 'truncate',
                            ellipsis: '…',
                            interval: 0,
                            rich: {
                                btnIcon: {
                                    width: 20,
                                    height: 20,
                                    lineHeight: 20,
                                    color: '#333',
                                    padding: [0, 8, 0, 0],
                                    fontSize: 26,
                                    align: 'right',
                                    backgroundColor: { image: btnIconIcon },
                                },
                            },
                        },
                    },
                    legend: {
                        show: true,
                        type: 'scroll',
                        orient: 'horizontal',
                        bottom: 0,
                        itemGap: 5,
                        pageIconSize: 10,
                        pageTextStyle: {
                            fontSize: 12,
                            color: '#333',
                        },
                    },
                    series: [],
                },
                fullscreenHeight: null,
            };
        },
        computed: {
            dateTimeFormat() {
                return {
                    hour: 'HH:mm',
                    day: 'dd MMM yyyy',
                    week: `w '${this.$t('week').toLowerCase()}' yyyy`,
                    month: 'MMM yyyy',
                    quarter: 'QQQ yyyy',
                    year: 'yyyy',
                };
            },
            locale() {
                return this.$i18n.locale;
            },
            isTimeType() {
                if (this.type === 'time') return true;
                if (this.$attrs.options?.xAxis?.type === 'time') return true;
                return false;
            },
            computedWidth() {
                return typeof this.width === 'number' ? `${this.width}px` : this.width;
            },
            computedHeight() {
                const optionsFromAttrs = this.$attrs.options || {};
                let heightInPixels = Number.parseInt(optionsFromAttrs.height);

                const mergedXAxisConfigs = Array.isArray(this.computedOptions.xAxis) ? this.computedOptions.xAxis : [this.computedOptions.xAxis || {}];

                const mergedYAxisConfigs = Array.isArray(this.computedOptions.yAxis) ? this.computedOptions.yAxis : [this.computedOptions.yAxis || {}];

                const seriesList = Array.isArray(optionsFromAttrs.series) ? optionsFromAttrs.series : [];

                const seriesTypeIsHorizontalCandidate = seriesItem => ['bar', 'pictorialBar', 'heatmap', 'rangeBar', 'custom'].includes(seriesItem.type);

                const seriesUsesCartesianGrid = seriesItem => (seriesItem.coordinateSystem ?? 'cartesian2d') === 'cartesian2d';

                const isXAxisTypeSupported = axisType => ['value', 'time', 'log', 'category', undefined].includes(axisType);

                // Czy mamy jakąkolwiek serię poziomą (Y = category na używanej osi)?
                const hasAnyHorizontalSeries = seriesList.some(seriesItem => {
                    if (!seriesTypeIsHorizontalCandidate(seriesItem) || !seriesUsesCartesianGrid(seriesItem)) return false;

                    const xAxisIndex = seriesItem.xAxisIndex ?? 0;
                    const yAxisIndex = seriesItem.yAxisIndex ?? 0;

                    const xAxisConfig = mergedXAxisConfigs[xAxisIndex] ?? mergedXAxisConfigs[0] ?? {};
                    const yAxisConfig = mergedYAxisConfigs[yAxisIndex] ?? mergedYAxisConfigs[0] ?? {};

                    const inferredYAxisType = yAxisConfig?.type ?? (Array.isArray(yAxisConfig?.data) ? 'category' : undefined);

                    return inferredYAxisType === 'category' && isXAxisTypeSupported(xAxisConfig?.type);
                });

                if (!heightInPixels && hasAnyHorizontalSeries) {
                    // Znajdź faktycznie używaną oś kategorii Y
                    let categoryYAxisConfig = null;
                    for (const seriesItem of seriesList) {
                        if (!seriesTypeIsHorizontalCandidate(seriesItem) || !seriesUsesCartesianGrid(seriesItem)) continue; // eslint-disable-line no-continue
                        const yAxisIndex = seriesItem.yAxisIndex ?? 0;
                        const candidateYAxis = mergedYAxisConfigs[yAxisIndex] ?? mergedYAxisConfigs[0] ?? {};
                        const inferredYAxisType = candidateYAxis?.type ?? (Array.isArray(candidateYAxis?.data) ? 'category' : undefined);
                        if (inferredYAxisType === 'category') {
                            categoryYAxisConfig = candidateYAxis;
                            break;
                        }
                    }
                    categoryYAxisConfig ||= mergedYAxisConfigs.find(axis => (axis?.type ?? (Array.isArray(axis?.data) ? 'category' : undefined)) === 'category') ?? mergedYAxisConfigs[0];

                    // Policz liczbę wierszy (kategorii)
                    const getCategoryRowCount = () => {
                        if (Array.isArray(categoryYAxisConfig?.data)) return categoryYAxisConfig.data.length;

                        // Heatmap: weź max indeksu Y z danych
                        const heatmapSeries = seriesList.find(seriesItem => seriesItem.type === 'heatmap' && Array.isArray(seriesItem.data));
                        if (heatmapSeries) {
                            const yIndices = heatmapSeries.data.map(dataPoint => (Array.isArray(dataPoint) ? dataPoint[1] : dataPoint?.value?.[1])).filter(index => Number.isFinite(index));
                            if (yIndices.length) return Math.max(...yIndices) + 1;
                        }

                        // RangeBar/Custom (Gantt): przyjmij liczbę serii jako liczbę wierszy
                        const ganttLikeSeries = seriesList.filter(seriesItem => seriesItem.type === 'rangeBar' || (seriesItem.type === 'custom' && seriesItem.encode?.y != null));
                        if (ganttLikeSeries.length) return ganttLikeSeries.length;

                        return 0;
                    };

                    const categoryRowCount = getCategoryRowCount();
                    const minimumChartHeight = 300;
                    const rowHeight = 40;
                    const labelsHeight = this.isTimeType ? 50 : 10;

                    const gridTop = Number.parseInt(this.computedOptions.grid?.top ?? 0);
                    const gridBottom = Number.parseInt(this.computedOptions.grid?.bottom ?? 0);

                    heightInPixels = categoryRowCount ? Math.max(100, categoryRowCount * rowHeight + labelsHeight + gridTop + gridBottom) : minimumChartHeight;
                }

                if (!heightInPixels) {
                    heightInPixels = Number.parseInt(this.height);
                }
                return heightInPixels;
            },
            computedOptions() {
                const effectiveLocale = getEffectiveLocale();

                // Bazowa struktura opcji wykresu
                const baseOptions = {
                    xAxis: {},
                    yAxis: {},
                    tooltip: {},
                };

                // Funkcja budująca opcje osi dla danych czasowych
                const buildTimeAxisOptions = () => ({
                    type: 'category',
                    axisLabel: {
                        formatter: value => this.$format.date(Number.parseInt(value), this.dateTimeFormat[this.groupBy]),
                        hideOverlap: true,
                        rotate: 45,
                    },
                    axisLine: {
                        show: true,
                    },
                    axisTick: {
                        show: true,
                    },
                });

                // Funkcja budująca opcje tooltipu dla danych czasowych
                const buildTimeTooltipOptions = () => ({
                    trigger: 'axis',
                    axisPointer: {
                        label: {
                            formatter: params => this.$format.date(Number.parseInt(params.value), this.dateTimeFormat[this.groupBy]),
                        },
                    },
                    valueFormatter: value => new Intl.NumberFormat(effectiveLocale).format(value || 0),
                });

                // Konfiguracja dla danych czasowych
                if (this.isTimeType) {
                    baseOptions.xAxis = mergeWith({}, baseOptions.xAxis, buildTimeAxisOptions(), customizer);
                    baseOptions.tooltip = mergeWith({}, baseOptions.tooltip, buildTimeTooltipOptions(), customizer);
                }

                // Wyłączenie linii osi dla wykresów typu "pie"
                if (Array.isArray(this.$attrs.options?.series) && this.$attrs.options.series.some(item => item.type === 'pie')) {
                    baseOptions.xAxis = mergeWith({}, baseOptions.xAxis, { axisLine: { show: false } }, customizer);
                    baseOptions.yAxis = mergeWith({}, baseOptions.yAxis, { axisLine: { show: false } }, customizer);
                    baseOptions.tooltip = mergeWith(
                        {},
                        baseOptions.tooltip,
                        { formatter: params => `${params.marker}<span style="margin-left:2px">${params.name}</span><span style="float:right;margin-left:20px;font-weight:900">${this.$format.number(params.value)} (${params.percent}%)</span>` },
                        customizer
                    );
                }

                // Ustawienie formatu etykiet dla osi Y, jeśli nie został określony formatter
                if (this.$attrs.options?.yAxis?.type === 'value') {
                    baseOptions.yAxis = mergeWith({}, baseOptions.yAxis, { axisLabel: { formatter: value => new Intl.NumberFormat(effectiveLocale).format(value) } }, customizer);
                }

                // Scalanie opcji: domyślne, obliczone oraz przekazane przez atrybuty
                const mergedOptions = mergeWith({}, this.defaultOptions, baseOptions, this.$attrs.options, customizer);

                // Konfiguracja opcji serii
                if (Array.isArray(mergedOptions.series)) {
                    const defaultLineOptions = {
                        type: 'line',
                        smooth: true,
                        showSymbol: false,
                        symbol: 'circle',
                        areaStyle: { opacity: 0.3 },
                    };

                    const defaultPieOptions = mergeWith(
                        {},
                        defaultLineOptions,
                        {
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                                },
                            },
                            radius: '83%',
                            center: ['50%', mergedOptions.legend?.show ? '46%' : '50%'],
                            label: {
                                show: true,
                                position: 'inside',
                                formatter: '{d}%',
                                color: '#fff',
                            },
                            labelLine: {
                                show: false,
                            },
                        },
                        customizer
                    );

                    mergedOptions.series = mergedOptions.series.map(seriesItem => {
                        if (seriesItem.type === 'pie') {
                            return mergeWith({}, defaultPieOptions, seriesItem, customizer);
                        }
                        return mergeWith({}, defaultLineOptions, seriesItem, customizer);
                    });
                }

                // Dostosowanie tooltipu dla wykresów liniowych
                if (mergedOptions.series.some(item => item.type === 'line')) {
                    mergedOptions.tooltip = mergeWith({}, mergedOptions.tooltip, { trigger: 'axis', axisPointer: { type: 'line' } }, customizer);
                }

                // Dostosowanie boundaryGap dla wykresów liniowych
                if (mergedOptions.series.every(item => item.type === 'line')) {
                    mergedOptions.xAxis = mergeWith({}, mergedOptions.xAxis, { boundaryGap: false }, customizer);
                }

                // Centralna funkcja do wyliczania grid na podstawie mergedOptions
                const computeGrid = mo => {
                    const baseGrid = mo.grid || {};
                    const yAxes = Array.isArray(mo.yAxis) ? mo.yAxis : [mo.yAxis];
                    const hasName = yAxes.some(axis => Boolean(axis?.name));

                    const gridOpts = {};
                    if (hasName) {
                        if (yAxes.some(axis => axis.position !== 'right')) gridOpts.left = 70;
                        if (yAxes.some(axis => axis.position === 'right')) gridOpts.right = 70;
                    } else {
                        gridOpts.containLabel = true;
                    }

                    const finalContainLabel = Boolean(gridOpts.containLabel ?? baseGrid.containLabel);

                    // Dostosowanie marginesu siatki, gdy legenda jest widoczna
                    if (mo.legend?.show) {
                        let bottom = 40;
                        if (!finalContainLabel) {
                            if (this.isTimeType) {
                                bottom = 100;
                            } else if ((mo.xAxis?.axisLabel?.rotate ?? 0) > 0) {
                                bottom = 80;
                            } else {
                                bottom = 60;
                            }
                        }
                        gridOpts.bottom = bottom;
                    } else if ((mo.xAxis?.axisLabel?.rotate ?? 0) > 0) {
                        if (!finalContainLabel) {
                            gridOpts.bottom = 40;
                        } else {
                            gridOpts.bottom = 10;
                        }
                    } else {
                        gridOpts.bottom = 10;
                    }

                    // Dostosowanie marginesu siatki, gdy jest title
                    if (mo.title?.text) {
                        gridOpts.top = 50;
                    }

                    return mergeWith({}, baseGrid, gridOpts, customizer);
                };

                // Ustaw finalny grid z wykorzystaniem computeGrid
                mergedOptions.grid = computeGrid(mergedOptions);

                if (mergedOptions.dataset?.some(item => item.transform?.type === 'percent')) {
                    mergedOptions.tooltip = mergeWith(
                        {},
                        mergedOptions.tooltip,
                        {
                            valueFormatter: value => this.$format.percent(value, 2),
                            formatter: params => {
                                const formatter = p =>
                                    `${p.marker}<span style="margin-left:2px">${p.seriesName}</span><span style="float:right;margin-left:20px;font-weight:900">${p.data.__raw[p.seriesName]} (${p.data[p.seriesName].toFixed(2)}%)</span>`;
                                if (Array.isArray(params)) {
                                    const header = `${params[0].axisValueLabel}<br/>`;
                                    const body = params.map(formatter).join('<br/>');
                                    return header + body;
                                }
                                return `${params.axisValueLabel}<br/>${formatter(params)}`;
                            },
                        },
                        customizer
                    );
                    mergedOptions.yAxis = mergeWith(
                        {},
                        mergedOptions.yAxis,
                        {
                            type: 'value',
                            min: 0,
                            max: 100,
                            interval: 10,
                            axisLabel: {
                                formatter: '{value}%',
                            },
                        },
                        customizer
                    );
                }

                // Przetwarzamy serie typu "rangeBar"
                if (mergedOptions.series.some(item => item.type === 'rangeBar')) {
                    // Tworzymy customowy tooltip
                    const tooltip = {
                        trigger: 'item',
                        formatter: params => {
                            const dataPoint = params.data;
                            const start = dataPoint.value[1];
                            const end = dataPoint.value[2];
                            const from = this.$format.date(Number.parseInt(start));
                            const to = this.$format.date(Number.parseInt(end));
                            const duration = this.$format.duration(start, end, { roundToUnit: 'day', roundingMethod: 'ceil' });
                            const color = params.color || '#000';
                            const seriesName = params.name || '';
                            return `
                                <div class="echarts-tooltip-rangebar">
                                <div style="margin-bottom: 5px">
                                    <strong class="series-name" style="color: ${color}">${seriesName}</strong>
                                </div>
                                <div>
                                    <span class="value start-value">${from}</span>
                                    <span class="separator">-</span>
                                    <span class="value end-value">${to}</span>
                                </div>
                                <div>
                                    <span class="value-period">
                                    <strong>${this.$t('duration')}:</strong> ${duration}
                                    </span>
                                </div>
                                </div>
                            `;
                        },
                    };
                    mergedOptions.tooltip = mergeWith({}, mergedOptions.tooltip, tooltip, customizer);

                    // Wyznaczamy listę kategorii na osi Y – nazwy produktów
                    const rangeBarSeries = (mergedOptions.series || []).filter(series => series.type === 'rangeBar');

                    // Kategorii Y używamy w dokładnie tej samej kolejności co serie rangeBar.
                    // Duplikaty nazw są dozwolone — ważna jest kolejność.
                    const categories = rangeBarSeries.map(series => series.name);

                    mergedOptions.yAxis = mergeWith({}, mergedOptions.yAxis, { type: 'category', data: categories }, customizer);

                    mergedOptions.series = mergedOptions.series.map(seriesItem => {
                        if (seriesItem.type !== 'rangeBar') return seriesItem;

                        // Unikalny indeks wiersza po pozycji serii wśród rangeBarów (NIE po nazwie)
                        const rangeIndex = rangeBarSeries.indexOf(seriesItem);

                        const seriesDataItem = seriesItem.data?.[0] || {};
                        const newData = (seriesDataItem.data || []).map(dp => {
                            const [start, end] = dp.y;
                            return { value: [rangeIndex, start, end, end - start], name: seriesItem.name };
                        });

                        const baseName = seriesItem.name;
                        const uniqueName = `${baseName}__${rangeIndex + 1}`; // unikalne name dla legendy

                        return {
                            ...seriesItem,
                            id: seriesItem.id ?? `range-${rangeIndex}`,
                            name: uniqueName, // ważne: unikalne, aby pokazać zduplikowane pozycje w legendzie
                            type: 'custom',
                            renderItem: this.renderRangeBarItem,
                            encode: { x: [1, 2], y: 0 },
                            data: newData,
                        };
                    });

                    // const grid = {
                    //     left: 10,
                    //     right: 10,
                    //     top: 10,
                    //     bottom: 40,
                    //     containLabel: true,
                    // };
                    // mergedOptions.grid = mergeWith({}, mergedOptions.grid, grid, customizer);
                    mergedOptions.legend = mergeWith(
                        {},
                        mergedOptions.legend,
                        {
                            show: true,
                            formatter: legendName => String(legendName).replace(/__\d+$/, ''), // pokazuj czystą nazwę bez sufiksu
                        },
                        customizer
                    );
                }

                // Tooltip dla "heatmap"
                if (mergedOptions.series?.some(seriesItem => seriesItem.type === 'heatmap')) {
                    // Pobranie etykiet z osi Y (pierwsza oś kategorii po lewej)
                    const yAxesArray = Array.isArray(mergedOptions.yAxis) ? mergedOptions.yAxis : [mergedOptions.yAxis];
                    const yAxisLabels = yAxesArray[0]?.data || [];
                    // Zachowanie istniejącego valueFormatter jeśli jest zdefiniowany
                    const formatValue = mergedOptions.tooltip?.valueFormatter || (rawValue => rawValue);
                    mergedOptions.tooltip = mergeWith(
                        {},
                        mergedOptions.tooltip,
                        {
                            trigger: 'item',
                            axisPointer: { type: 'shadow' },
                            formatter(tooltipParams) {
                                // W heatmapie value jest zwykle tablicą: [xIndex, yIndex, wartość]
                                const isArrayValue = Array.isArray(tooltipParams.value);
                                const yIndex = isArrayValue ? tooltipParams.value[1] : tooltipParams.data?.yIndex ?? tooltipParams.dataIndex;
                                // Pełna nazwa z osi Y
                                const yAxisNameRaw = yAxisLabels?.[yIndex];
                                const yAxisName = yAxisNameRaw != null ? decodeURIComponent(String(yAxisNameRaw)) : tooltipParams.axisValueLabel || '';
                                // Nazwa kategorii z osi X
                                const xAxisName = decodeURIComponent(String(tooltipParams.name ?? ''));
                                // Wartość z komórki
                                const cellValue = isArrayValue ? tooltipParams.value[2] : tooltipParams.value;
                                return `
                                    <div class="echarts-tooltip">
                                        <div style="margin-bottom:6px;"><strong>${yAxisName}</strong></div>
                                        <div>
                                            ${tooltipParams.marker}
                                            <span style="margin-left:2px">${xAxisName}</span>
                                            <span style="float:right;margin-left:20px;font-weight:900">${formatValue(cellValue)}</span>
                                        </div>
                                    </div>
                                `;
                            },
                        },
                        customizer
                    );
                }

                // usuń wycinki o wartości 0
                if (Array.isArray(mergedOptions.series)) {
                    mergedOptions.series = mergedOptions.series.map(series => {
                        if (series.type === 'pie' && Array.isArray(series.data)) {
                            return {
                                ...series,
                                data: series.data.filter(item => item.value > 0),
                            };
                        }
                        return series;
                    });
                }

                // Usuń wszystkie serie pie, które nie mają żadnych punktów
                if (Array.isArray(mergedOptions.series)) {
                    mergedOptions.series = mergedOptions.series.filter(s => {
                        // jeśli to pie, to zostaw tylko, gdy data istnieje i ma długość > 0
                        if (s.type === 'pie') {
                            return Array.isArray(s.data) && s.data.length > 0;
                        }
                        // inne typy serii pozostawiamy bez zmian
                        return true;
                    });
                }

                // Dodanie komponentu graphic wyświetlającego "Brak danych", gdy nie ma żadnych serii
                if (!mergedOptions.series || mergedOptions.series.length === 0 || (!mergedOptions.dataset?.length && mergedOptions.series.every(item => !item.data?.length))) {
                    mergedOptions.graphic = [
                        {
                            type: 'text',
                            left: 'center',
                            top: 'middle',
                            style: {
                                text: this.$t('noData'),
                                fill: '#ccc',
                                font: '30px sans-serif',
                            },
                        },
                    ];
                    mergedOptions.grid = { left: 5, right: 5, top: 20, bottom: 40, containLabel: false };
                    mergedOptions.xAxis = { show: false };
                    mergedOptions.yAxis = { show: false };
                } else {
                    // W przeciwnym razie graphic jest pusty
                    mergedOptions.graphic = [];
                }

                {
                    const userGrid = this.$attrs?.options?.grid;
                    if (userGrid != null) {
                        // Przypadek 1: jeden grid jako obiekt
                        if (!Array.isArray(userGrid)) {
                            // user określa tylko to, co chce zmienić; reszta zostaje z Twoich wyliczeń
                            mergedOptions.grid = mergeWith({}, mergedOptions.grid ?? {}, userGrid);
                        } else {
                            // Przypadek 2: multi-grid (tablica)
                            // Mergujemy element-po-elemencie, aby wartości usera wygrały,
                            // a brakujące pola zostały z konfiguracji.
                            const baseGridArray = Array.isArray(mergedOptions.grid) ? mergedOptions.grid : [mergedOptions.grid ?? {}];
                            mergedOptions.grid = userGrid.map((userGridItem, index) => mergeWith({}, baseGridArray[index] ?? {}, userGridItem));
                        }
                    }
                }

                // pokaż symbol gdy seria liniowa ma tylko 1 punkt
                if (Array.isArray(mergedOptions.series)) {
                    mergedOptions.series = mergedOptions.series.map(seriesItem => {
                        if (seriesItem.type !== 'line') return seriesItem;
                        const points = Array.isArray(seriesItem.data) ? seriesItem.data : [];
                        if (points.length === 1) {
                            return mergeWith(
                                {},
                                seriesItem,
                                {
                                    showSymbol: true,
                                    symbol: 'circle',
                                    symbolSize: 7,
                                    showAllSymbol: true,
                                },
                                customizer
                            );
                        }
                        return seriesItem;
                    });
                }

                delete mergedOptions.height;

                mergedOptions.groupBy = this.groupBy; // neded to refresh chart

                return mergedOptions;
            },
            attrs() {
                let height = this.fullscreenHeight || this.computedHeight;
                height = typeof height === 'number' ? `${height}px` : height;
                const attrs = { ...this.$attrs };
                delete attrs.options;
                return {
                    ...attrs,
                    option: this.computedOptions,
                    ref: 'chart',
                    style: {
                        width: this.computedWidth,
                        height,
                    },
                    autoresize: true,
                    manualUpdate: true,
                };
            },
            listeners() {
                return { ...this.$listeners, finished: this.eventFinished };
            },
        },
        watch: {
            locale: {
                handler() {
                    const effectiveLocale = getEffectiveLocale();
                    if (effectiveLocale === 'pl') echarts.registerLocale('PL', plLocale);
                    if (effectiveLocale === 'en-GB') echarts.registerLocale('EN', enLocale);
                },
                immediate: true,
            },
            computedOptions(newOptions, oldOptions) {
                if (JSON.stringify(newOptions || {}) !== JSON.stringify(oldOptions || {})) {
                    this.updating = true;
                    this.$nextTick(() => {
                        const chart = this.$refs.chart.chart;
                        chart.setOption(newOptions, { replaceMerge: ['graphic', 'series', 'xAxis', 'yAxis'] });
                    });
                }
            },
        },
        methods: {
            exportChart() {
                const url = this.$refs.chart.chart.getDataURL({
                    type: 'png',
                    pixelRatio: 2,
                    backgroundColor: '#fff',
                });
                const name = (this.header || 'chart').toLowerCase().replaceAll(' ', '-');
                this.$utils.exportData(url, name);
            },
            fullscreenChart() {
                const container = this.$refs.chartContainer.$el;
                if (!document.fullscreenElement) {
                    container.requestFullscreen({ navigationUI: 'show' });
                } else {
                    document.exitFullscreen();
                }
            },
            adjustChartHeight(e) {
                if (document.fullscreenElement && e.type === 'fullscreenchange' && e.target === this.$refs.chartContainer?.$el) {
                    setTimeout(() => {
                        const containerHeight = this.$refs.chartContainer?.$el?.offsetHeight || 0;
                        this.fullscreenHeight = Math.max(Math.min(800, containerHeight - 80), this.computedHeight);
                    }, 100);
                } else {
                    this.fullscreenHeight = null;
                }
            },
            eventFinished() {
                this.updating = false;
            },
            getRangeBarCategories(apexData) {
                // Wyznaczamy unikalne kategorie - używamy nazwy serii
                const categories = apexData.map(series => series.name);
                return categories;
            },
            // Metoda do renderowania elementów serii custom
            renderRangeBarItem(params, api) {
                const categoryIndex = api.value(0);
                const startCoord = api.coord([api.value(1), categoryIndex]);
                const endCoord = api.coord([api.value(2), categoryIndex]);
                const height = api.size([0, 1])[1] * 0.6; // 60% wysokości kategorii
                const rectShape = echarts.graphic.clipRectByRect(
                    {
                        x: startCoord[0],
                        y: startCoord[1] - height / 2,
                        width: endCoord[0] - startCoord[0],
                        height,
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    }
                );
                return (
                    rectShape && {
                        type: 'rect',
                        shape: rectShape,
                        style: api.style(),
                    }
                );
            },
        },
        mounted() {
            document.addEventListener('fullscreenchange', this.adjustChartHeight);
        },
        beforeUnmount() {
            document.removeEventListener('fullscreenchange', this.adjustChartHeight);
        },
    };
</script>
